import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Settings as SettingsIcon, Info } from "lucide-react";
import { getSettings, updateSettings } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { MobileMenu, MobileTabBar } from "@/components/AppNav";
import { AppHeader } from "@/components/brand/AppHeader";
import { SubmissionSettings } from "@/components/SubmissionSettings";
import { LegalDocuments } from "@/components/legal/LegalDocuments";
import { VideoSettings } from "@/components/video/VideoSettings";
import { WebsiteFeedKey, NEWSLETTER_KEY } from "@/components/WebsiteFeedKey";
import { TestModeCard } from "@/components/TestMode";
import { HistoricalImport } from "@/components/HistoricalImport";
import { BidSmsSettings } from "@/components/BidSmsSettings";
import { SyncScheduleSettings } from "@/components/sync/SyncScheduleSettings";

export default function AdSettings() {
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setForm(await getSettings());
      } catch {
        toast.error("Could not load Ad Settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateSettings({
        default_sale_close_time: form.default_sale_close_time,
        default_time_zone: form.default_time_zone,
        brokerage_statement: form.brokerage_statement,
        auto_include_brokerage: form.auto_include_brokerage,
        allow_seller_sale_dates: !!form.allow_seller_sale_dates,
      });
      setForm(updated);
      toast.success("Ad Settings saved", {
        description: "Applies to future assembled ads. Existing finalized write-ups are untouched.",
      });
    } catch {
      toast.error("Could not save Ad Settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] pb-40 md:pb-0 overflow-x-clip">
      <AppHeader title="Ad Settings" onBack={() => navigate("/")} backTestId="back-to-horses" width="max-w-3xl">
          <MobileMenu />
      </AppHeader>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-6">
        <div className="flex items-start gap-2 text-sm text-stone-600 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6">
          <Info className="h-4 w-4 mt-0.5 text-amber-800 shrink-0" />
          <p>
            These defaults apply to <strong>future assembled ads</strong>. Changing them never
            modifies an existing finalized advertisement.
          </p>
        </div>

        {loading || !form ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}
          </div>
        ) : (
          <div data-testid="ad-settings-form" className="space-y-6">
            <TestModeCard />
            <BidSmsSettings />
            <SyncScheduleSettings />
            <SubmissionSettings />
            <LegalDocuments />
            <VideoSettings />
            <WebsiteFeedKey />
            <WebsiteFeedKey cfg={NEWSLETTER_KEY} />
            <HistoricalImport />
            {/* Bidding close time */}
            <section className="rounded-xl border border-stone-200 bg-white p-5">
              <h2 className="font-semibold text-stone-900 mb-1">Bidding Close Time</h2>
              <p className="text-xs text-stone-500 mb-4">
                Default closing time applied to every horse's bidding end, unless an administrator
                enters a manual Sale Close Time on the horse.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5">
                    Default Sale Close Time
                  </label>
                  <Input
                    data-testid="default-close-time"
                    value={form.default_sale_close_time || ""}
                    onChange={(e) => set("default_sale_close_time", e.target.value)}
                    placeholder="8:00 PM"
                    className="border-stone-200 focus-visible:ring-amber-800"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5">
                    Default Time Zone
                  </label>
                  <Input
                    data-testid="default-time-zone"
                    value={form.default_time_zone || ""}
                    onChange={(e) => set("default_time_zone", e.target.value)}
                    placeholder="America/Edmonton"
                    className="border-stone-200 focus-visible:ring-amber-800"
                  />
                  <p className="text-[11px] text-stone-400 mt-1">
                    IANA zone — daylight saving (MST/MDT) handled automatically.
                  </p>
                </div>
              </div>
            </section>

            {/* Seller sale-date permission */}
            <section className="rounded-xl border border-stone-200 bg-white p-5">
              <h2 className="font-semibold text-stone-900 mb-1">Seller Sale Dates</h2>
              <p className="text-xs text-stone-500 mb-4">When OFF, only admins schedule Bidding Start and Close dates (Sale Scheduling on each application). When ON, sellers can pick their dates in the Ad Template; admins can still override. Sellers can never change the 8:00 PM Mountain closing time.</p>
              <div className="flex items-center gap-2">
                <Switch data-testid="allow-seller-sale-dates" checked={!!form.allow_seller_sale_dates} onCheckedChange={(v) => set("allow_seller_sale_dates", v)} />
                <span className="text-sm text-stone-700 font-medium">Allow Sellers to Choose Sale Dates</span>
              </div>
            </section>

            {/* Brokerage statement */}
            <section className="rounded-xl border border-stone-200 bg-white p-5">
              <h2 className="font-semibold text-stone-900 mb-1">Canadian Horse Brokerage Statement</h2>
              <p className="text-xs text-stone-500 mb-4">
                Shown at the bottom of every Canadian horse ad (after all contact info). The seller
                pays the brokerage fees.
              </p>
              <div className="flex items-center gap-2 mb-4">
                <Switch
                  data-testid="auto-include-brokerage"
                  checked={!!form.auto_include_brokerage}
                  onCheckedChange={(v) => set("auto_include_brokerage", v)}
                />
                <span className="text-sm text-stone-600">
                  Automatically include brokerage statement for Canadian horses
                </span>
              </div>
              <label className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5">
                Statement Text
              </label>
              <Textarea
                data-testid="brokerage-statement"
                value={form.brokerage_statement || ""}
                onChange={(e) => set("brokerage_statement", e.target.value)}
                rows={4}
                className="border-stone-200 focus-visible:ring-amber-800 text-sm"
              />
            </section>

            <div className="fixed bottom-[56px] inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-stone-200 p-3 md:static md:bg-transparent md:border-0 md:p-0 flex justify-end">
              <Button
                data-testid="save-settings-button"
                onClick={save}
                disabled={saving}
                className="w-full md:w-auto h-12 md:h-10 rounded-full bg-amber-800 hover:bg-amber-900 text-white px-6"
              >
                {saving ? "Saving…" : "Save Settings"}
              </Button>
            </div>
          </div>
        )}
      </main>
      <MobileTabBar />
    </div>
  );
}
